import { ComponentProps, ComponentType, forwardRef, ReactNode } from "react";
import { cn } from "@/lib/utils"; // Assuming you have a cn utility function

interface AppShellProps extends ComponentProps<"div"> {
  sidebar?: ReactNode;
  header?: ReactNode;
}

export const AppShell = forwardRef<HTMLDivElement, AppShellProps>(
  ({ sidebar, header, ...props }, ref) => {
    return (
      <div ref={ref} {...props} className={cn("flex min-h-screen items-start", props.className)}>
        {sidebar}

        <div className="flex min-w-0 grow flex-col self-stretch">
          {header}
          {props.children}
        </div>
      </div>
    );
  },
);

export type PageMainPadding = "none" | "small" | "medium" | "large";

// Convert spacing values to Tailwind classes or CSS values
const paddingX = {
  fb: {
    small: "30px",
    medium: "45px 35px", // theme.spacing(45, 35)
    large: "55px 35px", // theme.spacing(55, 35)
  },
  lg: {
    small: "45px",
    medium: "90px 70px", // theme.spacing(90, 70)
    large: "110px 70px", // theme.spacing(110, 70)
  },
} as const;

const paddingY = {
  fb: {
    small: "15px",
    medium: "25px 15px", // theme.spacing(25, 15)
    large: "45px 20px", // theme.spacing(45, 20)
  },
  lg: {
    small: "20px",
    medium: "50px 30px", // theme.spacing(50, 30)
    large: "90px 30px", // theme.spacing(90, 30)
  },
} as const;

interface PageMainContentProps extends AppShellProps {
  ps?: PageMainPadding;
  psY?: PageMainPadding;
  psX?: PageMainPadding;
  scrollable?: boolean;
}

const getPaddingY = (size: PageMainPadding, screenSize?: keyof typeof paddingY): string =>
  size === "none"
    ? "0"
    : screenSize && screenSize in paddingY
      ? paddingY[screenSize][size]
      : paddingY.fb[size];

const getPaddingX = (size: PageMainPadding, screenSize?: keyof typeof paddingX): string =>
  size === "none"
    ? "0"
    : screenSize && screenSize in paddingX
      ? paddingX[screenSize][size]
      : paddingX.fb[size];

export const PageMainContent: ComponentType<PageMainContentProps> = forwardRef<
  HTMLDivElement,
  PageMainContentProps
>(({ ps = "small", psY, psX, scrollable, className, style, children, ...props }, ref) => {
  const paddingInline = getPaddingX(psX ?? ps);
  const paddingBlock = getPaddingY(psY ?? ps);
  // const paddingInlineLg = getPaddingX(psX ?? ps, "lg");
  // const paddingBlockLg = getPaddingY(psY ?? ps, "lg");

  return (
    <div
      ref={ref}
      className={cn("flex h-full w-full flex-col", scrollable && "overflow-y-auto", className)}
      style={{
        paddingInline,
        paddingBlock,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
});

export default AppShell;

export const getPagePadding = {
  y: getPaddingY,
  x: getPaddingX,
};
